import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Avatar, Button, SparkIcon, Text } from '@spark';

import SendIcon from '@/shared/assets/icons/send.svg?react';
import SparkStar from '@/shared/assets/icons/spark-star.svg?react';

import styles from './SparkAssistantPanel.module.css';

export const SPARK_ASSISTANT_TITLE = 'Specific AI Assistant';
export const SPARK_ASSISTANT_INPUT_PLACEHOLDER = 'Ask me anything';
export const SPARK_ASSISTANT_CLOSE_LABEL = 'Close assistant';
export const SPARK_ASSISTANT_SEND_LABEL = 'Send';

/** Initials-style fallback when the signed-in user has no display name. */
export const SPARK_ASSISTANT_DEFAULT_USER_NAME = 'You';

/** Spark's yellow star, shown left of every assistant bubble (Figma 9844:25195). */
function StarMark() {
    return (
        <span className={styles.star} aria-hidden="true">
            <SparkStar className={styles.starSvg} focusable="false" />
        </span>
    );
}

function TypingBubble() {
    return (
        <div className={`${styles.row} ${styles.rowAssistant}`}>
            <StarMark />
            <div className={styles.bubbleColumn}>
                <div
                    className={`${styles.bubble} ${styles.bubbleAssistant}`}
                    data-testid="spark-assistant-typing"
                    role="status"
                    aria-label="Spark is typing"
                >
                    <span className={styles.typing} aria-hidden="true">
                        <span className={styles.typingDot} />
                        <span className={styles.typingDot} />
                        <span className={styles.typingDot} />
                    </span>
                </div>
            </div>
        </div>
    );
}

/**
 * One assistant turn: the blue bubble holds the text, then either the live
 * option buttons or — once the user picked one — that pick as a chip (Figma
 * 9894:38572), which is the user's message for that turn.
 */
function AssistantMessage({ message, showOptions, optionsDisabled, onSelectOption, selection }) {
    const options = showOptions ? message.options || [] : [];
    return (
        <div className={`${styles.row} ${styles.rowAssistant}`}>
            <StarMark />
            <div className={styles.bubbleColumn}>
                <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
                    <div className={styles.bubbleText} data-testid="spark-assistant-message" data-role="assistant">
                        {message.content}
                    </div>
                    {options.length ? (
                        <div className={styles.options} data-testid="spark-assistant-options">
                            {options.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={styles.option}
                                    data-testid="spark-assistant-option"
                                    data-option-id={option.id}
                                    disabled={optionsDisabled}
                                    onClick={() => onSelectOption?.(option)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    {selection ? (
                        <div className={styles.selection} data-testid="spark-assistant-message" data-role="user">
                            {selection.content}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function UserMessage({ message, user }) {
    const displayName = user?.name || user?.email || SPARK_ASSISTANT_DEFAULT_USER_NAME;
    return (
        <div className={`${styles.row} ${styles.rowUser}`}>
            <div className={`${styles.bubble} ${styles.bubbleUser}`} data-testid="spark-assistant-message" data-role="user">
                {message.content}
            </div>
            <span className={styles.avatar} data-testid="spark-assistant-avatar">
                <Avatar
                    type={user?.picture ? 'image' : 'initials'}
                    src={user?.picture || undefined}
                    alt={displayName}
                    primaryText={displayName}
                    size="small"
                />
            </span>
        </div>
    );
}

/** A user message created by clicking an option renders inside the bubble that offered it. */
const isOptionPick = (message) => message?.role === 'user' && message.optionId != null;

/**
 * The chat column. Presentation only — state comes from `useSparkAssistant`.
 *
 * @param {object} props
 * @param {Array} props.messages `{id, role, content, options?, optionId?}`
 * @param {number} props.latestAssistantIndex only this message shows option buttons
 * @param {boolean} props.loading shows the "..." typing bubble
 * @param {(text: string) => void} props.onSend
 * @param {(option: {id: string, label: string}) => void} props.onSelectOption
 * @param {() => void} props.onClose
 * @param {object} [props.user] `{ name, email, picture }` for the user avatar
 */
export default function SparkAssistantPanel({
    messages = [],
    latestAssistantIndex = -1,
    loading = false,
    onSend,
    onSelectOption,
    onClose,
    user,
    className = '',
}) {
    const [draft, setDraft] = useState('');
    const listRef = useRef(null);
    const inputRef = useRef(null);

    // Keep the newest message in view.
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        if (typeof list.scrollTo === 'function') {
            list.scrollTo({ top: list.scrollHeight });
        } else {
            list.scrollTop = list.scrollHeight;
        }
    }, [messages, loading]);

    // Single-line input that grows with the draft (up to the CSS max-height).
    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        input.style.height = 'auto';
        input.style.height = `${input.scrollHeight}px`;
    }, [draft]);

    const submit = useCallback(() => {
        const text = draft.trim();
        if (!text) return;
        onSend?.(text);
        setDraft('');
    }, [draft, onSend]);

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
                event.preventDefault();
                submit();
            }
        },
        [submit]
    );

    // Option buttons stay usable on the latest assistant message until the
    // user answers. A typing bubble (a follow-up that may say nothing) must
    // not disable them — they are the question being asked.
    const lastMessage = messages[messages.length - 1];
    const optionsLive = lastMessage?.role === 'assistant';

    return (
        <aside
            className={[styles.panel, className].filter(Boolean).join(' ')}
            data-testid="spark-assistant-panel"
            aria-label={SPARK_ASSISTANT_TITLE}
        >
            <header className={styles.header}>
                <Text as="h2" variant="heading-5-semibold" className={styles.title}>
                    {SPARK_ASSISTANT_TITLE}
                </Text>
                <Button
                    size="small"
                    variant="ghost"
                    icon={<SparkIcon slug="close" size="small" />}
                    aria-label={SPARK_ASSISTANT_CLOSE_LABEL}
                    tooltip={SPARK_ASSISTANT_CLOSE_LABEL}
                    data-testid="spark-assistant-close"
                    onClick={onClose}
                />
            </header>

            <div className={styles.body}>
                <div className={styles.messages} ref={listRef} data-testid="spark-assistant-messages">
                    <div className={styles.thread}>
                        {messages.map((message, index) => {
                            if (isOptionPick(message)) {
                                // Rendered as the chip inside the previous assistant bubble.
                                if (messages[index - 1]?.role === 'assistant') return null;
                                return <UserMessage key={message.id} message={message} user={user} />;
                            }
                            if (message.role === 'user') {
                                return <UserMessage key={message.id} message={message} user={user} />;
                            }
                            const next = messages[index + 1];
                            return (
                                <AssistantMessage
                                    key={message.id}
                                    message={message}
                                    showOptions={index === latestAssistantIndex}
                                    optionsDisabled={!optionsLive}
                                    onSelectOption={onSelectOption}
                                    selection={isOptionPick(next) ? next : null}
                                />
                            );
                        })}
                        {loading ? <TypingBubble /> : null}
                    </div>
                </div>

                <div className={styles.composer}>
                    <textarea
                        ref={inputRef}
                        className={styles.input}
                        placeholder={SPARK_ASSISTANT_INPUT_PLACEHOLDER}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        aria-label={SPARK_ASSISTANT_INPUT_PLACEHOLDER}
                        data-testid="spark-assistant-input"
                    />
                    <button
                        type="button"
                        className={styles.send}
                        aria-label={SPARK_ASSISTANT_SEND_LABEL}
                        title={SPARK_ASSISTANT_SEND_LABEL}
                        data-testid="spark-assistant-send"
                        disabled={!draft.trim()}
                        onClick={submit}
                    >
                        <SendIcon className={styles.sendIcon} aria-hidden="true" focusable="false" />
                    </button>
                </div>
            </div>
        </aside>
    );
}
